import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RowSelectionState } from '@tanstack/react-table'
import { useDataTableContext } from 'components/DataTable'
import { CompanyTypeSlug, TableTypeSlug } from 'models/companies'
import { DealCreate } from 'models/deals'
import { useCallback, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { z } from 'zod'
import {
  DEAL_TABLE_KEY,
  getCampaignTableKey,
} from '../../../constants/tableQueryKeys'
import { Modal } from '../../UI/Modal/Modal'
import confirm from 'components/dialogConfirm'
import DealDetailsStep from './DealDetailsStep'
import {
  checkDoorSelectionRules,
  selectDoorsStepFormSchema,
  SelectDoorsStepWithControl,
} from './SelectDoorsStep'

const dealsFormSchema = z
  .object({
    deals: z.array(
      z.object({
        create: z.boolean(),
        product: z.number(),
        product_variant: z.number().optional().nullable(),
        monthly_volume_override: z.number().min(0).optional().nullable(),
        monthly_revenue_override: z.number().min(0).optional().nullable(),
        close_date_override: z.string().optional().nullable(),
        account_owner: z.number().optional().nullable(),
        sales_stage: z.number().optional().nullable(),
        origin_campaign_id: z.number().optional().nullable(),
        name: z
          .union([
            z.string().max(100, 'Deal name must be 100 characters or less'),
            z.null(),
            z.undefined(),
          ])
          .optional()
          .nullable(),
      })
    ),
  })
  .merge(selectDoorsStepFormSchema)
  .superRefine((v, ctx) => {
    const dealsToCreate = v.deals.filter((deal) => deal.create)

    if (dealsToCreate.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one deal must be selected',
        path: ['custom'],
      })
    }

    checkDoorSelectionRules(v, ctx)
  })

export type DealsFormValues = z.infer<typeof dealsFormSchema>

export interface CreateDealModalForTableProps {
  data?: any[]
  idAccessor?: string
  doorIdAccessor?: string
  targetCompanyId?: undefined
  handleClose: () => void
  onDealCreated?: () => void
  show: boolean
  submitButtonText?: string
  cancelButtonText?: string
  // These relate to optimistic UI updates
  queryKey?: string[]
  optimisticUiTarget:
    | 'companies'
    | 'chains'
    | 'create-company'
    | 'deals-section'
    | 'k12'
    | 'none'
  filterAndSortParams?: Record<string, any>
  originCampaignId?: number
  // FIR-7420: prefill defaults when opened from the sequencing replies inbox.
  // The signed-in user becomes the account owner; the originating reply
  // conversation (BisonReplyThread pk) is stamped on every created deal so the
  // reply thread can render a "Linked to <deal>" badge.
  initialAccountOwnerId?: number
  sourceReplyThreadId?: number
  // Preselect (check) the matching product row on open, e.g. the reply's
  // campaign product when creating from the sequencing inbox (FIR-7420).
  initialSelectedProductId?: number
  companyTypeSlug?: CompanyTypeSlug
  tableTypeSlug: TableTypeSlug
  forDoorSelection?: boolean
}

export interface CreateDealModalProps extends Omit<
  CreateDealModalForTableProps,
  'data' | 'targetCompanyId'
> {
  targetCompanyId: number | undefined
  targetDoorProxyId?: number // This is the door proxy ID, not the firstbite_id
  totalSelectedRows: number
  existingDealsByProduct?: Record<number, number>
  selectedRows?: any[]
  unselectedRows?: any[]
  rowSelection?: RowSelectionState
  isUserCreated?: boolean
  isAllRowsSelected?: boolean
}

export function CreateDealModalForTable(props: CreateDealModalForTableProps) {
  const {
    state: { rowSelection, isAllRowsSelected, totalSelectedRows },
  } = useDataTableContext()
  const unselectedRows = props?.data?.filter((_, i) => !rowSelection[i])
  const selectedRows = props?.data?.filter((_, i) => rowSelection[i])

  // {product_id: amount_of_deals}
  const existingDealsByProduct = useMemo(() => {
    const out: Record<number, number> = {}
    selectedRows?.forEach((i) =>
      i?.deals?.forEach((j: any) => {
        out[j.product] = (out?.[j.product] ?? 0) + 1
      })
    )
    return out
  }, [selectedRows])

  return (
    <CreateDealModal
      {...props}
      targetCompanyId={undefined}
      selectedRows={selectedRows}
      unselectedRows={unselectedRows}
      rowSelection={rowSelection}
      existingDealsByProduct={existingDealsByProduct}
      totalSelectedRows={totalSelectedRows}
      isAllRowsSelected={isAllRowsSelected}
    />
  )
}

// ---------------------------------------------------------------------------
// Shell: fetches predictions, shows spinner until ready, then renders form
// ---------------------------------------------------------------------------

export default function CreateDealModal(props: CreateDealModalProps) {
  const api = useApiService()

  const chainIdAccessor = useMemo(() => {
    return props.idAccessor
  }, [props.idAccessor])

  const doorIdAccessor = useMemo(() => {
    return props.doorIdAccessor ?? 'id'
  }, [props.doorIdAccessor])

  const idAccessorToUse = useMemo(() => {
    return props.forDoorSelection ? doorIdAccessor : chainIdAccessor
  }, [props.forDoorSelection, doorIdAccessor, chainIdAccessor])

  const targetCompanyId = useMemo(() => {
    if (props.targetCompanyId) {
      return props.targetCompanyId
    }
    if (props.selectedRows?.length === 1) {
      return chainIdAccessor
        ?.split('.')
        .reduce((acc, key) => acc[key], props.selectedRows[0])
    }
    return undefined
  }, [props.targetCompanyId, props.selectedRows, chainIdAccessor])

  const { data: predictions, isLoading } = api.useGetProductPredictions({
    chainProxyId: targetCompanyId,
    noCache: true,
  })

  // Get company details for the placeholder
  const { data: companyDetails } = api.useGetCompany(targetCompanyId || 0)

  const { data: { count: companyTotalDoors } = { count: 0 } } =
    api.useGetDoorsForSelectionCount(targetCompanyId ?? 0)

  const shouldRequestForDoorSelection = !!targetCompanyId
  const totalSteps = useMemo(() => {
    if (props.forDoorSelection) {
      return 1
    }
    return shouldRequestForDoorSelection ? 2 : 1
  }, [shouldRequestForDoorSelection, props.forDoorSelection])

  // Show deal name field only when one row is selected (for opportunities table)
  // or always show it for company/door pages
  const showDealName = useMemo(() => {
    if (props.targetCompanyId || props.targetDoorProxyId) {
      return true
    }
    return props.totalSelectedRows === 1
  }, [props.targetCompanyId, props.targetDoorProxyId, props.totalSelectedRows])

  if (!props.show) return null

  if (isLoading) {
    return (
      <Modal
        open={true}
        onOpenChange={(open) => !open && props.handleClose()}
        size="xl"
        title="Add Deals"
        onCancel={props.handleClose}
        description={
          <span className="block max-w-[800px]">
            Deals allow you to track specific revenue opportunities throughout
            your sales pipeline. Each deal is done on a per product basis. For
            deals with unmatched companies, you can provide your own volume,
            revenue, and close date estimates.
          </span>
        }
      >
        <div className="d-flex justify-content-center overflow-hidden">
          <Spinner animation="border" />
        </div>
      </Modal>
    )
  }

  // Omit targetCompanyId from props to avoid duplicate with the computed value

  const { targetCompanyId: _omitTargetCompanyId, ...restProps } = props

  return (
    <CreateDealFormContent
      key={`${targetCompanyId}-${predictions?.length}`}
      predictions={predictions}
      companyDetails={companyDetails}
      companyTotalDoors={companyTotalDoors}
      totalSteps={totalSteps}
      targetCompanyId={targetCompanyId}
      idAccessorToUse={idAccessorToUse}
      showDealName={showDealName}
      {...restProps}
    />
  )
}

// ---------------------------------------------------------------------------
// Form content: receives ready data, initializes form directly, no sync effects
// ---------------------------------------------------------------------------

interface CreateDealFormContentProps extends Omit<
  CreateDealModalProps,
  'targetCompanyId'
> {
  predictions: any
  companyDetails: any
  companyTotalDoors: number
  totalSteps: number
  targetCompanyId: number | undefined
  idAccessorToUse: string | undefined
  showDealName: boolean
}

/* eslint-disable react/prop-types -- TypeScript handles prop validation via CreateDealFormContentProps */
function CreateDealFormContent(allProps: CreateDealFormContentProps) {
  const {
    predictions,
    companyDetails,
    companyTotalDoors,
    totalSteps,
    targetCompanyId,
    idAccessorToUse,
    showDealName,
    ...props
  } = allProps
  const [currentStep, setCurrentStep] = useState(1)
  const queryClient = useQueryClient()
  const api = useApiService()

  // Compute initial deals from predictions - no useEffect needed
  const initialDeals = useMemo(() => {
    if (!predictions) return []
    return predictions.map((prediction: any) => ({
      // Preselect the campaign's product row when opened from a reply (FIR-7420).
      create:
        props.initialSelectedProductId != null &&
        prediction.product.id === props.initialSelectedProductId,
      product: prediction.product.id,
      product_variant: null,
      monthly_volume_override: null,
      monthly_revenue_override: null,
      close_date_override: null,
      origin_campaign_id: props.originCampaignId,
      account_owner: props.initialAccountOwnerId ?? null,
      name: null,
    }))
  }, [
    predictions,
    props.originCampaignId,
    props.initialAccountOwnerId,
    props.initialSelectedProductId,
  ])

  // Form initialized with computed data - no sync useEffects
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    trigger,
    setValue,
  } = useForm({
    resolver: zodResolver(dealsFormSchema),
    defaultValues: {
      deals: initialDeals,
      apply_to: props.targetDoorProxyId ? 'select_locations' : 'entire_company',
      door_ids: props.targetDoorProxyId ? [props.targetDoorProxyId] : [],
    } as DealsFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  })

  const mutation = useMutation({
    mutationFn: async ({
      deals,
      excludeIds,
      selectedIds,
      applyTo,
      doorIds,
    }: {
      deals: Partial<DealCreate>[]
      applyTo?: string
      doorIds?: number[]
      excludeIds: string[]
      selectedIds: string[]
      affectedRowsIndexes: number[]
    }) => {
      let body: any = {}

      if (targetCompanyId && !props.forDoorSelection) {
        body['include_ids'] = [targetCompanyId]
      } else if (
        props.forDoorSelection &&
        props.targetDoorProxyId &&
        targetCompanyId
      ) {
        body['include_ids'] = [props.targetDoorProxyId]
        body['apply_to'] = 'select_locations'
      } else {
        body['include_ids'] = selectedIds
        body['exclude_ids'] = excludeIds

        if (props.isAllRowsSelected) {
          body['include_ids'] = []
        }
      }

      body = {
        deals,
        filter_params: props.filterAndSortParams ?? {},
        include_ids: body.include_ids,
        exclude_ids: body.exclude_ids,
        ...(body.apply_to && { apply_to: body.apply_to }),
      }

      if (!props.forDoorSelection) {
        body['apply_to'] = applyTo
        body['door_ids'] = doorIds
      }

      return await api.commonCreateDeals(props.tableTypeSlug, {
        ...body,
      })
    },
    onError: (err, newTodo, context) => {
      if (!context) return
      toast.error('Error creating deals')
      handleClose()
    },
    onSuccess: (data) => {
      if (data.status === 202) {
        toast.info(
          data.data?.message ??
            'We are processing your request, you will be notified when it is done'
        )
      } else {
        toast.success('Deals created successfully')
        props.onDealCreated?.()
        queryClient.invalidateQueries({ queryKey: props.queryKey })
        queryClient.invalidateQueries({ queryKey: [DEAL_TABLE_KEY] })
        if (props.originCampaignId) {
          queryClient.invalidateQueries({
            queryKey: [
              getCampaignTableKey(DEAL_TABLE_KEY, props.originCampaignId),
            ],
          })
        }
      }
      handleClose()
    },
  })

  const handleClose = () => {
    props.handleClose()
    setCurrentStep(1)
  }

  const onAccept = async () => {
    const valid = await trigger()
    if (!valid) return

    if (currentStep === totalSteps) {
      return onSubmit()
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  const onBack = useCallback(() => {
    if (currentStep === 1) {
      handleClose()
    } else {
      setCurrentStep(1)
    }
  }, [currentStep])

  const onSubmit = handleSubmit(
    async (values) => {
      const dealsToCreate: Partial<DealCreate>[] = values.deals
        .filter((deal) => deal.create)
        .map((deal) => {
          return {
            product_id: deal.product,
            product_variant_id: deal.product_variant ?? undefined,
            monthly_volume_override: deal.monthly_volume_override ?? undefined,
            monthly_revenue_override:
              deal.monthly_revenue_override ?? undefined,
            close_date_override: deal.close_date_override || undefined,
            sales_stage_id: deal.sales_stage ?? undefined,
            account_owner_id: deal.account_owner ?? undefined,
            origin_campaign_id: deal.origin_campaign_id ?? undefined,
            source_reply_thread_id: props.sourceReplyThreadId ?? undefined,
            name: deal.name || undefined,
          }
        })

      const excludeIds =
        props.unselectedRows?.map((r) => {
          return idAccessorToUse?.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []

      const selectedIds =
        props.selectedRows?.map((r) => {
          return idAccessorToUse?.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []

      const affectedRowsIndexes = props.rowSelection
        ? Object.keys(props.rowSelection).map((key) => parseInt(key))
        : []

      if (props.totalSelectedRows > 1000) {
        const confirmed = await confirm(
          `You are about to create ${props.totalSelectedRows} deals. This is a large number and may take a while to complete. Are you sure you want to proceed?`,
          'Create Deals in Bulk'
        )
        if (!confirmed) return
      }

      await mutation.mutateAsync({
        deals: dealsToCreate,
        applyTo: values.apply_to,
        doorIds: values.door_ids,
        excludeIds,
        selectedIds,
        affectedRowsIndexes,
      })
    },
    (err) => console.warn(err)
  )

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      size="xl"
      onAccept={onAccept}
      onCancel={onBack}
      preventCloseOnCancel
      loading={isSubmitting}
      cancelButtonProps={{
        type: 'button',
      }}
      title="Add Deals"
      acceptButtonText={
        currentStep === totalSteps
          ? (props.submitButtonText ?? 'Create Deals')
          : 'Next'
      }
      acceptButtonProps={{
        disabled: !isValid,
      }}
      cancelButtonText={
        currentStep === totalSteps && totalSteps > 1
          ? 'Back'
          : (props.cancelButtonText ?? 'Cancel')
      }
      description={
        <span className="block max-w-[800px]">
          Deals allow you to track specific revenue opportunities throughout
          your sales pipeline. Each deal is done on a per product basis. For
          deals with unmatched companies, you can provide your own volume,
          revenue, and close date estimates.
        </span>
      }
    >
      {currentStep === 1 && (
        <DealDetailsStep
          predictions={predictions}
          control={control}
          setValue={setValue}
          companyTypeSlug={props.companyTypeSlug}
          existingDealsByProduct={props.existingDealsByProduct}
          showDealName={showDealName}
          companyDetails={companyDetails}
          initialSelectedProductId={props.initialSelectedProductId}
        />
      )}
      {currentStep === 2 && (
        <SelectDoorsStepWithControl
          control={control}
          chainProxyId={targetCompanyId!}
          companyTotalDoors={companyTotalDoors}
          formTrigger={trigger}
        />
      )}
    </Modal>
  )
}
